<template>
  <TableRow class="border-[#4C4C4C]">
    <TableCell class="font-medium py-2">
      {{ name }}
    </TableCell>
    <TableCell class="text-right py-2">
      <div v-if="isExpired">
        <Badge variant="secondary">Expired</Badge>
      </div>
      <div v-else class="flex justify-end gap-2">
        <AlertDialog>
          <AlertDialogTrigger as-child>
            <Button type="button" size="sm" variant="outline"> Reject </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            </AlertDialogHeader>
            <div class="px-7">
              <AlertDialogDescription>
                This action cannot be undone.
              </AlertDialogDescription>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel as-child>
                <Button variant="ghost">Cancel</Button>
              </AlertDialogCancel>
              <AlertDialogAction as-child>
                <Button
                  variant="danger"
                  :disabled="isDeclinePending"
                  @click="onDeclineInvitation"
                >
                  Decline
                </Button>
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <Button
          type="button"
          size="sm"
          :disabled="isAcceptPending"
          @click="onAcceptInvitation"
        >
          Accept invite
        </Button>
      </div>
    </TableCell>
  </TableRow>
</template>

<script setup>
import { useRouter } from 'vue-router';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { X } from 'lucide-vue-next';

import { Button } from '@ui/components/button';
import { Badge } from '@ui/components/badge';
import { IconButton } from '@ui/components/icon-button';
import { TableCell, TableRow } from '@ui/components/table';
import { toast } from 'vue-sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@ui/components/alert-dialog';

import requests from '@vuex/requests.js';

const router = useRouter();
const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  isExpired: {
    type: Boolean,
    required: true,
  },
});

const queryClient = useQueryClient();

const { isPending: isAcceptPending, mutate: acceptInvitation } = useMutation({
  mutationFn: async () => {
    await requests.invitations.accept({ id: props.id });
  },
  onSuccess: () => {
    router.push({
      name: 'workspace',
    });
  },
  onError: err => {
    if (err.response?.data?.exception?.includes('CardException')) {
      toast.error(
        `${err.response.data.message} Please contact the owner of the workspace.`
      );
      return;
    }
    toast.error(err.response?.data?.message || 'An error occurred');
  },
});

const { isPending: isDeclinePending, mutate: declineInvitation } = useMutation({
  mutationFn: async () => {
    await requests.invitations.decline({ id: props.id });
  },
  onSuccess: async () => {
    await queryClient.invalidateQueries({ queryKey: ['pendingInvitations'] });
  },
});

const onAcceptInvitation = () => acceptInvitation();
const onDeclineInvitation = () => declineInvitation();
</script>
